/**
 * PlatformBridge instance for the running app.
 *
 * If the renderer is loaded inside the Electron desktop shell, the preload
 * script (packages/desktop/src/preload/index.ts) attaches `window.desktop`,
 * and we install the desktop variant — silent print, native dialogs, and
 * OS-secure key/value storage (Step 6 of the build instructions).
 *
 * Otherwise we fall back to the browser implementation: print dialog,
 * `<a download>`, `<input type=file>`, localStorage.
 *
 * React components MUST go through this singleton — never `window.desktop`,
 * never `ipcRenderer`, never anything Electron-specific.
 */

import {
  createDesktopPlatformBridge,
  createWebPlatformBridge,
  getDesktopBridgeApi,
  PlatformBridge,
} from '@forgedas/shared-core';

const APP_VERSION =
  process.env.REACT_APP_VERSION ||
  // CRA injects npm package version when REACT_APP_VERSION is not set.
  process.env.npm_package_version ||
  '0.0.0';

const desktopApi = typeof window !== 'undefined' ? getDesktopBridgeApi() : null;

export const platformBridge: PlatformBridge = desktopApi
  ? createDesktopPlatformBridge({ bridge: desktopApi })
  : createWebPlatformBridge({
      appVersion: APP_VERSION,
    });

/** True when running inside the Electron desktop shell. */
export const isDesktop: boolean = platformBridge.isDesktop;

export type { PlatformBridge };
